import type {
  AssignStageTeamRequest,
  CreateSchedulerTeamRequest,
  CreateWorkSessionRequest,
  CreateWorkSessionsRequest,
  CreateWorkSessionsResponse,
  CreateWorkerLeaveRequestInput,
  CreateWorkerRequest,
  EnrollSchedulerProjectRequest,
  PreviewSchedulerEnrollmentRequest,
  ReopenSchedulerProjectRequest,
  ResetSchedulerProjectHoursRequest,
  SchedulerApplyRescueRequestInput,
  SchedulerBoardResponse,
  UpdateSchedulerAutoProgressRequest,
  UpdateSchedulerCalendarRequestInput,
  SchedulerCompletePreviewResponse,
  SchedulerCompleteTimesheetResponse,
  SchedulerPausePreviewResponse,
  SchedulerPauseStageRequest,
  SchedulerCompleteStageRequest,
  SchedulerEnrollmentPreviewResponse,
  SchedulerProjectAttendanceResponse,
  SchedulerProjectHistoryResponse,
  SchedulerStaffingRequestInput,
  SchedulerStaffingResponse,
  SchedulerStartNowPreviewResponse,
  SchedulerStartNowRequest,
  SchedulerWhatIfRequestInput,
  SchedulerWhatIfResponse,
  UpdateSchedulerProjectRequest,
  UpdateSchedulerTeamRequest,
  UpdateTeamManualHoursRequest,
  UpdateWorkSessionRequest,
  UpdateWorkerRequest,
  WorkerRosterResponse,
  CollaboratorEngagementsResponse,
  TimesheetResponse,
  UpsertCollaboratorEngagementRequest,
  WorkerSessionsResponse,
  WorkerStatsResponse,
  ProjectHoursResponse,
  ProjectStatsResponse,
} from '@kreart/shared';
import { api } from '@/lib/api';

export const operationsSchedulerApi = {
  getBoard: () => api.get<SchedulerBoardResponse>('/operations-scheduler/board'),
  enrollProject: (body: EnrollSchedulerProjectRequest) =>
    api.post<SchedulerBoardResponse>('/operations-scheduler/projects', body),
  previewEnrollment: (body: PreviewSchedulerEnrollmentRequest) =>
    api.post<SchedulerEnrollmentPreviewResponse>('/operations-scheduler/projects/preview', body),
  /** Board re-projected with hypothetical extra teams — the Gantt's "after" lane. */
  whatIfPreview: (body: SchedulerWhatIfRequestInput) =>
    api.post<SchedulerWhatIfResponse>('/operations-scheduler/what-if', body),
  /**
   * "Recomandă echipele": how many crews, on which operations, in the day or
   * in a second shift, would get every deadline project on time. Read-only.
   */
  staffingRecommendation: (body: SchedulerStaffingRequestInput) =>
    api.post<SchedulerStaffingResponse>('/operations-scheduler/what-if/staffing', body),
  updateProject: (schedulerProjectId: string, body: UpdateSchedulerProjectRequest) =>
    api.patch<SchedulerBoardResponse>(`/operations-scheduler/projects/${schedulerProjectId}`, body),
  removeProject: (schedulerProjectId: string) =>
    api.delete<SchedulerBoardResponse>(`/operations-scheduler/projects/${schedulerProjectId}`),
  /**
   * "Resetează orele": every operation back to 0 h and its start, the bonus
   * credited for it taken back, the project off the board, behind "Pornește" —
   * and its timesheet entries kept on it, taken off it or deleted, as chosen.
   */
  resetProjectHours: (schedulerProjectId: string, body: ResetSchedulerProjectHoursRequest = {}) =>
    api.post<SchedulerBoardResponse>(
      `/operations-scheduler/projects/${schedulerProjectId}/reset-hours`,
      body,
    ),
  /** Off the board and into "Proiecte în programare". */
  holdProject: (schedulerProjectId: string) =>
    api.post<SchedulerBoardResponse>(`/operations-scheduler/projects/${schedulerProjectId}/hold`),
  /**
   * Put a finished project under warranty back into the flow for the chosen
   * operations only. The intervention is a NEW scheduler row; the production
   * row it reopens is never written again.
   */
  reopenProject: (schedulerProjectId: string, body: ReopenSchedulerProjectRequest) =>
    api.post<SchedulerBoardResponse>(
      `/operations-scheduler/projects/${schedulerProjectId}/reopen`,
      body,
    ),
  /** Gone when nothing was recorded on it; otherwise kept CANCELLED with its minutes. */
  cancelIntervention: (interventionId: string) =>
    api.post<SchedulerBoardResponse>(
      `/operations-scheduler/projects/${interventionId}/cancel-intervention`,
    ),
  /** Production run and every intervention; the id of either row answers the same. */
  getProjectHistory: (schedulerProjectId: string) =>
    api.get<SchedulerProjectHistoryResponse>(
      `/operations-scheduler/projects/${schedulerProjectId}/history`,
    ),
  /** Per operation: the flow's tally and the timesheet hours behind it, by person. */
  getProjectAttendance: (schedulerProjectId: string) =>
    api.get<SchedulerProjectAttendanceResponse>(
      `/operations-scheduler/projects/${schedulerProjectId}/attendance`,
    ),
  /** `next` hands the operation that becomes next to a picked crew (managers only). */
  completeStage: (stageId: string, body: SchedulerCompleteStageRequest = {}) =>
    api.post<SchedulerBoardResponse>(`/operations-scheduler/stages/${stageId}/complete`, body),
  /** What "Done" would lead to, and who could take what comes next. */
  previewCompleteStage: (stageId: string) =>
    api.post<SchedulerCompletePreviewResponse>(
      `/operations-scheduler/stages/${stageId}/complete/preview`,
    ),
  /** The total worked on the operation, and what "Salvează pontaj?" offers. */
  getCompleteTimesheet: (stageId: string) =>
    api.get<SchedulerCompleteTimesheetResponse>(
      `/operations-scheduler/stages/${stageId}/complete/timesheet`,
    ),
  /** What "Pune în așteptare" would free, for its question. */
  previewPauseStage: (stageId: string) =>
    api.post<SchedulerPausePreviewResponse>(
      `/operations-scheduler/stages/${stageId}/pause/preview`,
    ),
  pauseStage: (stageId: string, body: SchedulerPauseStageRequest = {}) =>
    api.post<SchedulerBoardResponse>(`/operations-scheduler/stages/${stageId}/pause`, body),
  resumeStage: (stageId: string) =>
    api.post<SchedulerBoardResponse>(`/operations-scheduler/stages/${stageId}/resume`),
  previewStartNow: (stageId: string) =>
    api.post<SchedulerStartNowPreviewResponse>(
      `/operations-scheduler/stages/${stageId}/start-now/preview`,
    ),
  /** Without a pick, the suggested crew is used. */
  startNow: (stageId: string, body: SchedulerStartNowRequest = {}) =>
    api.post<SchedulerBoardResponse>(`/operations-scheduler/stages/${stageId}/start-now`, body),
  confirmProposal: (proposalId: string) =>
    api.post<SchedulerBoardResponse>(
      `/operations-scheduler/proposals/${encodeURIComponent(proposalId)}/confirm`,
    ),
  dismissProposal: (proposalId: string) =>
    api.post<SchedulerBoardResponse>(
      `/operations-scheduler/proposals/${encodeURIComponent(proposalId)}/dismiss`,
    ),
  dismissAlert: (alertId: string) =>
    api.post<SchedulerBoardResponse>(
      `/operations-scheduler/alerts/${encodeURIComponent(alertId)}/dismiss`,
    ),
  /**
   * "Aplică" on a deadline recommendation: the ticked steps, written together.
   * The server re-checks them against the board as it is now and refuses the
   * lot (409 SCHEDULER_RESCUE_REJECTED, `details.problems`) rather than apply a
   * plan the manager did not see.
   */
  applyRescue: (schedulerProjectId: string, body: SchedulerApplyRescueRequestInput) =>
    api.post<SchedulerBoardResponse>(
      `/operations-scheduler/rescue/${schedulerProjectId}/apply`,
      body,
    ),
  /**
   * Take a crew off an operation's plan: a planned crew no longer joins it, and
   * a reserved collaborator is released (back to inactive if the recommendation
   * had brought it in).
   */
  removePlannedTeam: (stageId: string, teamId: string) =>
    api.delete<SchedulerBoardResponse>(
      `/operations-scheduler/stages/${stageId}/planned-teams/${teamId}`,
    ),
  createTeam: (body: CreateSchedulerTeamRequest) =>
    api.post<SchedulerBoardResponse>('/operations-scheduler/teams', body),
  updateTeam: (teamId: string, body: UpdateSchedulerTeamRequest) =>
    api.patch<SchedulerBoardResponse>(`/operations-scheduler/teams/${teamId}`, body),
  updateTeamManualHours: (teamId: string, body: UpdateTeamManualHoursRequest) =>
    api.patch<SchedulerBoardResponse>(`/operations-scheduler/teams/${teamId}/manual-hours`, body),
  /** Zero one team's counters, or every team's when no id is given. */
  resetTeamHours: (teamId?: string) =>
    api.post<SchedulerBoardResponse>(
      teamId
        ? `/operations-scheduler/teams/${teamId}/reset-hours`
        : '/operations-scheduler/teams/reset-hours',
    ),
  deleteTeam: (teamId: string) =>
    api.delete<SchedulerBoardResponse>(`/operations-scheduler/teams/${teamId}`),
  assignStageTeam: (stageId: string, body: AssignStageTeamRequest) =>
    api.post<SchedulerBoardResponse>(`/operations-scheduler/stages/${stageId}/teams`, body),
  unassignStageTeam: (stageId: string, teamId: string) =>
    api.delete<SchedulerBoardResponse>(`/operations-scheduler/stages/${stageId}/teams/${teamId}`),
  resetClock: () => api.post<SchedulerBoardResponse>('/operations-scheduler/clock/reset'),
  // "Zile și program de lucru": the shop's calendar, and optionally the
  // general program given to some workers. Answers with the refreshed board.
  updateCalendar: (body: UpdateSchedulerCalendarRequestInput) =>
    api.put<SchedulerBoardResponse>('/operations-scheduler/calendar', body),
  // "Fluxul numără singur orele" on or off. Answers with the refreshed board.
  updateAutoProgress: (body: UpdateSchedulerAutoProgressRequest) =>
    api.put<SchedulerBoardResponse>('/operations-scheduler/auto-progress', body),

  // Collaborators — external crews paid per project. Days are derived server
  // side from the operations they worked, so nothing here computes them.
  getCollaboratorEngagements: () =>
    api.get<CollaboratorEngagementsResponse>('/operations-scheduler/collaborators/engagements'),
  upsertCollaboratorEngagement: (body: UpsertCollaboratorEngagementRequest) =>
    api.post<CollaboratorEngagementsResponse>(
      '/operations-scheduler/collaborators/engagements',
      body,
    ),
  deleteCollaboratorEngagement: (engagementId: string) =>
    api.delete<CollaboratorEngagementsResponse>(
      `/operations-scheduler/collaborators/engagements/${engagementId}`,
    ),

  // Workers & attendance — a real-time roster ledger, separate from the board.
  getRoster: () => api.get<WorkerRosterResponse>('/operations-scheduler/workers'),
  createWorker: (teamId: string, body: CreateWorkerRequest) =>
    api.post<WorkerRosterResponse>(`/operations-scheduler/teams/${teamId}/workers`, body),
  updateWorker: (workerId: string, body: UpdateWorkerRequest) =>
    api.patch<WorkerRosterResponse>(`/operations-scheduler/workers/${workerId}`, body),
  /**
   * Without `purgeHistory` this 409s (`SCHEDULER_WORKER_HAS_HISTORY`) for anyone
   * who ever clocked in, so the caller can confirm the loss before repeating the
   * call with it set.
   */
  deleteWorker: (workerId: string, purgeHistory: boolean) =>
    api.delete<WorkerRosterResponse>(
      `/operations-scheduler/workers/${workerId}${purgeHistory ? '?purgeHistory=true' : ''}`,
    ),
  /**
   * Annual leave, whole days with both ends inclusive. It changes how fast the
   * worker's crew works on those days everywhere in the scheduler, so the
   * server settles the crew's running work first and answers with the roster;
   * an overlap with another of the worker's leaves is a 409
   * (SCHEDULER_LEAVE_OVERLAP).
   */
  createWorkerLeave: (workerId: string, body: CreateWorkerLeaveRequestInput) =>
    api.post<WorkerRosterResponse>(`/operations-scheduler/workers/${workerId}/leaves`, body),
  deleteWorkerLeave: (leaveId: string) =>
    api.delete<WorkerRosterResponse>(`/operations-scheduler/worker-leaves/${leaveId}`),
  startSession: (workerId: string) =>
    api.post<WorkerRosterResponse>(`/operations-scheduler/workers/${workerId}/sessions/start`),
  stopSession: (workerId: string) =>
    api.post<WorkerRosterResponse>(`/operations-scheduler/workers/${workerId}/sessions/stop`),
  startAllForTeam: (teamId: string) =>
    api.post<WorkerRosterResponse>(`/operations-scheduler/teams/${teamId}/sessions/start-all`),
  stopAllForTeam: (teamId: string) =>
    api.post<WorkerRosterResponse>(`/operations-scheduler/teams/${teamId}/sessions/stop-all`),
  // Manual attendance: corrections are made against sessions, because the day
  // rollup is derived from them — see WorkerAttendanceService.listSessions.
  getWorkerSessions: (workerId: string, day?: string) =>
    api.get<WorkerSessionsResponse>(
      `/operations-scheduler/workers/${workerId}/sessions${day ? `?day=${day}` : ''}`,
    ),
  // A period is Romanian dates, both inclusive: a day, a week, a month, an interval.
  getTimesheet: (from: string, to: string) =>
    api.get<TimesheetResponse>(`/operations-scheduler/timesheet?from=${from}&to=${to}`),
  getProjectHours: (from: string, to: string) =>
    api.get<ProjectHoursResponse>(`/operations-scheduler/project-hours?from=${from}&to=${to}`),
  createWorkSession: (workerId: string, body: CreateWorkSessionRequest) =>
    api.post<WorkerSessionsResponse>(`/operations-scheduler/workers/${workerId}/sessions`, body),
  /** The same hours for several people at once — all saved, or none. */
  createWorkSessions: (body: CreateWorkSessionsRequest) =>
    api.post<CreateWorkSessionsResponse>('/operations-scheduler/sessions', body),
  updateWorkSession: (sessionId: string, body: UpdateWorkSessionRequest) =>
    api.patch<WorkerSessionsResponse>(`/operations-scheduler/sessions/${sessionId}`, body),
  deleteWorkSession: (sessionId: string) =>
    api.delete<WorkerSessionsResponse>(`/operations-scheduler/sessions/${sessionId}`),
  getWorkerStats: (workerId: string, year: number) =>
    api.get<WorkerStatsResponse>(`/operations-scheduler/stats/worker/${workerId}?year=${year}`),
  getProjectStats: (projectId: string, year: number) =>
    api.get<ProjectStatsResponse>(`/operations-scheduler/stats/project/${projectId}?year=${year}`),
};
